import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useBrandingContext } from '../contexts/BrandingContext';
import { AppLayout } from '../components/AppLayout';
import { SettingsService } from '../services/settingsService';
import { ImageUploadService } from '../services/imageUploadService';
import { MaterialService } from '../services/materialService';
import { LaborService } from '../services/laborService';
import { PDFTemplateSettings } from '../components/PDFTemplateSettings';
import { RoleSettings } from '../components/RoleSettings';
import { CompanySettings, Category, LaborRate } from '../types';

type SettingsTab = 'branding' | 'colors' | 'typography' | 'quotes' | 'materials' | 'labor' | 'email' | 'pdf' | 'roles';

export const AdminSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { refreshBranding } = useBrandingContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<SettingsTab>('branding');

  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  const [categories, setCategories] = useState<Category[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');

  const [laborRates, setLaborRates] = useState<LaborRate[]>([]);
  const [showLaborForm, setShowLaborForm] = useState(false);
  const [editingLaborId, setEditingLaborId] = useState<string | null>(null);
  const [laborFormData, setLaborFormData] = useState<{
    name: string;
    description: string;
    rateType: 'hourly' | 'per_sqft';
    hourlyRate: string;
    perSqftRate: string;
    markupFactor: string;
    isActive: boolean;
  }>({
    name: '',
    description: '',
    rateType: 'hourly',
    hourlyRate: '',
    perSqftRate: '',
    markupFactor: '2.0',
    isActive: true,
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadSettings();
    loadCategories();
    loadLaborRates();
  }, [user, navigate]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await SettingsService.getSettings();
      setSettings(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await MaterialService.getCategories();
      setCategories(data);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const loadLaborRates = async () => {
    try {
      const data = await LaborService.getAllLaborRates();
      setLaborRates(data);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAddLaborRate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!laborFormData.name.trim()) return;

    try {
      setError(null);
      const highestOrder = Math.max(...laborRates.map(r => r.order || 0), 0);

      const rateData: Omit<LaborRate, 'id'> = {
        name: laborFormData.name.trim(),
        description: laborFormData.description.trim(),
        rateType: laborFormData.rateType as 'hourly' | 'per_sqft',
        hourlyRate: (laborFormData.rateType as string) === 'hourly' ? parseFloat(laborFormData.hourlyRate) || 0 : undefined,
        perSqftRate: (laborFormData.rateType as string) === 'per_sqft' ? parseFloat(laborFormData.perSqftRate) || 0 : undefined,
        markupFactor: parseFloat(laborFormData.markupFactor) || 2.0,
        isActive: laborFormData.isActive,
        order: highestOrder + 1,
      };

      if (editingLaborId) {
        await LaborService.updateLaborRate(editingLaborId, rateData);
        setSuccess('Labor rate updated successfully');
      } else {
        await LaborService.createLaborRate(rateData);
        setSuccess('Labor rate created successfully');
      }

      resetLaborForm();
      await loadLaborRates();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEditLaborRate = (rate: LaborRate) => {
    setLaborFormData({
      name: rate.name,
      description: rate.description,
      rateType: rate.rateType as 'hourly' | 'per_sqft',
      hourlyRate: String(rate.hourlyRate || ''),
      perSqftRate: String(rate.perSqftRate || ''),
      markupFactor: String(rate.markupFactor),
      isActive: rate.isActive,
    });
    setEditingLaborId(rate.id);
    setShowLaborForm(true);
  };

  const handleDeleteLaborRate = async (id: string) => {
    const rate = laborRates.find(r => r.id === id);
    if (!window.confirm(`Delete "${rate?.name}" labor rate?`)) return;

    try {
      setError(null);
      await LaborService.deleteLaborRate(id);
      setSuccess('Labor rate deleted successfully');
      await loadLaborRates();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const resetLaborForm = () => {
    setLaborFormData({
      name: '',
      description: '',
      rateType: 'hourly',
      hourlyRate: '',
      perSqftRate: '',
      markupFactor: '2.0',
      isActive: true,
    });
    setEditingLaborId(null);
    setShowLaborForm(false);
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;

    try {
      setError(null);
      const highestOrder = Math.max(...categories.map(c => c.order || 0), 0);
      await MaterialService.createCategory({
        name: newCategoryName.trim(),
        order: highestOrder + 1,
        parentCategoryId: null,
      });
      setNewCategoryName('');
      setSuccess('Category created successfully');
      await loadCategories();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    const categoryName = categories.find(c => c.id === categoryId)?.name;
    if (!window.confirm(`Delete category "${categoryName}"?`)) return;

    try {
      setError(null);
      await MaterialService.deleteCategory(categoryId);
      setSuccess('Category deleted successfully');
      await loadCategories();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    console.log('Logo upload initiated, file:', file?.name);
    if (!file || !settings) {
      console.log('Missing file or settings');
      return;
    }

    try {
      setUploadingLogo(true);
      setError(null);
      setSuccess(null);

      console.log('Starting logo upload...');

      // Delete old logo if it exists and is not a base64 image
      if (settings.branding.logoUrl && !settings.branding.logoUrl.startsWith('data:')) {
        try {
          console.log('Deleting old logo...');
          await ImageUploadService.deleteImage(settings.branding.logoUrl);
        } catch (deleteErr) {
          console.warn('Could not delete old logo:', deleteErr);
          // Continue with upload even if delete fails
        }
      }

      // Upload new logo to Firebase Storage
      console.log('Uploading new logo to Firebase Storage...');
      const logoUrl = await ImageUploadService.uploadLogo(file);
      console.log('Logo uploaded, URL:', logoUrl);

      // Update settings with new logo URL and save to Firestore
      const newSettings = {
        ...settings,
        branding: {
          ...settings.branding,
          logoUrl,
        },
      };

      console.log('Saving to Firestore...');
      setSettings(newSettings);
      await SettingsService.updateSettings(newSettings);
      console.log('Refreshing branding...');
      await refreshBranding();

      console.log('Upload complete!');
      setSuccess('Logo uploaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error uploading logo:', err);
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    console.log('Favicon upload initiated, file:', file?.name);
    if (!file || !settings) {
      console.log('Missing file or settings');
      return;
    }

    try {
      setUploadingFavicon(true);
      setError(null);
      setSuccess(null);

      console.log('Starting favicon upload...');

      // Delete old favicon if it exists and is not a base64 image
      if (settings.branding.faviconUrl && !settings.branding.faviconUrl.startsWith('data:')) {
        try {
          console.log('Deleting old favicon...');
          await ImageUploadService.deleteImage(settings.branding.faviconUrl);
        } catch (deleteErr) {
          console.warn('Could not delete old favicon:', deleteErr);
          // Continue with upload even if delete fails
        }
      }

      // Upload new favicon to Firebase Storage
      console.log('Uploading new favicon to Firebase Storage...');
      const faviconUrl = await ImageUploadService.uploadFavicon(file);
      console.log('Favicon uploaded, URL:', faviconUrl);

      // Update settings with new favicon URL and save to Firestore
      const newSettings = {
        ...settings,
        branding: {
          ...settings.branding,
          faviconUrl,
        },
      };

      console.log('Saving to Firestore...');
      setSettings(newSettings);
      await SettingsService.updateSettings(newSettings);
      console.log('Refreshing branding...');
      await refreshBranding();

      console.log('Upload complete!');
      setSuccess('Favicon uploaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error uploading favicon:', err);
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleChange = (
    section: keyof CompanySettings,
    field: string,
    value: any
  ) => {
    if (!settings) return;

    setSettings((prev) => {
      if (!prev) return prev;

      if (
        section === 'branding' ||
        section === 'colors' ||
        section === 'typography' ||
        section === 'quotes' ||
        section === 'email'
      ) {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value,
          },
        };
      }

      return prev;
    });
  };

  const handleNestedChange = (
    section: keyof CompanySettings,
    parent: string,
    field: string,
    value: any
  ) => {
    if (!settings) return;

    setSettings((prev) => {
      if (!prev) return prev;

      if (section === 'typography') {
        const typographySection = prev[section] as any;
        return {
          ...prev,
          [section]: {
            ...typographySection,
            [parent]: {
              ...typographySection[parent],
              [field]: value,
            },
          },
        };
      }

      return prev;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;

    try {
      setError(null);
      setSuccess(null);

      await SettingsService.updateSettings(settings);

      // Refresh branding context to apply changes immediately to all pages
      await refreshBranding();

      setSuccess('Settings updated successfully!');

      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <p>Loading settings...</p>
      </div>
    );
  }

  if (!settings) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <p>Failed to load settings</p>
      </div>
    );
  }

  return (
    <AppLayout>
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      <h1 style={{ margin: 0, marginBottom: '24px' }}>Company Settings</h1>

      {/* Settings Tabs */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          borderBottom: '2px solid var(--border-color)',
        }}
      >
        {(['branding', 'colors', 'typography', 'quotes', 'materials', 'labor', 'email', 'pdf', 'roles'] as SettingsTab[]).map(
          (tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '12px 16px',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab ? '3px solid var(--primary-button)' : 'none',
                color: activeTab === tab ? 'var(--primary-button)' : 'var(--secondary-text)',
                cursor: 'pointer',
                fontWeight: activeTab === tab ? '600' : '500',
                fontSize: '14px',
                transition: 'all 0.2s',
              }}
            >
              {tab === 'pdf' ? 'PDF Templates' : tab === 'roles' ? 'User Roles' : tab === 'materials' ? 'Material Categories' : tab === 'labor' ? 'Labor Management' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          )
        )}
      </div>

      {error && (
        <div
          style={{
            padding: '12px 16px',
            background: 'rgba(239, 68, 68, 0.1)',
            color: 'var(--error-color)',
            marginBottom: '20px',
            borderRadius: '6px',
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            padding: '12px 16px',
            background: 'rgba(16, 185, 129, 0.1)',
            color: 'var(--success-color)',
            marginBottom: '20px',
            borderRadius: '6px',
          }}
        >
          {success}
        </div>
      )}

      {activeTab === 'materials' ? (
        <div>
          {/* Materials Tab - Separate from main form */}
          <div
            style={{
              background: 'var(--card-background)',
              padding: '20px',
              marginBottom: '24px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
            }}
          >
            <h2 style={{ marginBottom: '16px', fontSize: '18px', color: 'var(--main-text)' }}>Material Categories</h2>

            {/* Add Category Form */}
            <div style={{ marginBottom: '24px', padding: '16px', background: 'var(--page-background)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
              <h3 style={{ margin: '0 0 12px 0', color: 'var(--main-text)', fontSize: '14px', fontWeight: '600' }}>Add New Category</h3>
              <form onSubmit={handleAddCategory} style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="e.g., Metal, Glass, Leather"
                  required
                  style={{
                    flex: 1,
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'var(--main-background)',
                    color: 'var(--main-text)',
                    boxSizing: 'border-box',
                  }}
                />
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    background: 'var(--primary-button)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                  }}
                >
                  Add
                </button>
              </form>
            </div>

            {/* Categories List */}
            <div>
              <h3 style={{ margin: '0 0 12px 0', color: 'var(--main-text)', fontSize: '14px', fontWeight: '600' }}>Categories ({categories.length})</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {categories.length === 0 ? (
                  <p style={{ color: 'var(--secondary-text)', margin: 0 }}>No categories yet</p>
                ) : (
                  categories.map((cat) => (
                    <div
                      key={cat.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        background: 'var(--main-background)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        color: 'var(--main-text)',
                      }}
                    >
                      <span style={{ fontSize: '14px' }}>{cat.name}</span>
                      <button
                        onClick={() => handleDeleteCategory(cat.id)}
                        type="button"
                        style={{
                          padding: '0 4px',
                          background: 'none',
                          border: 'none',
                          color: '#dc2626',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          lineHeight: '1',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title="Delete category"
                      >
                        ✕
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      ) : activeTab === 'labor' ? (
        <div>
          {/* Labor Management Tab - Separate from main form */}
          <div
            style={{
              background: 'var(--card-background)',
              padding: '20px',
              marginBottom: '24px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
            }}
          >
            <h2 style={{ marginBottom: '16px', fontSize: '18px', color: 'var(--main-text)' }}>Labor Management</h2>

            {/* Add Labor Rate Form */}
            <div style={{ marginBottom: '24px', padding: '16px', background: 'var(--page-background)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
              <h3 style={{ margin: '0 0 12px 0', color: 'var(--main-text)', fontSize: '14px', fontWeight: '600' }}>
                {editingLaborId ? 'Edit Labor Rate' : 'Add New Labor Rate'}
              </h3>
              <form onSubmit={handleAddLaborRate} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'var(--main-text)', fontWeight: '600', fontSize: '14px' }}>Rate Name *</label>
                  <input
                    type="text"
                    value={laborFormData.name}
                    onChange={(e) => setLaborFormData({ ...laborFormData, name: e.target.value })}
                    placeholder="e.g., Installation, General Labor"
                    required
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--main-background)', color: 'var(--main-text)', boxSizing: 'border-box', fontSize: '14px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'var(--main-text)', fontWeight: '600', fontSize: '14px' }}>Description (optional)</label>
                  <input
                    type="text"
                    value={laborFormData.description}
                    onChange={(e) => setLaborFormData({ ...laborFormData, description: e.target.value })}
                    placeholder="e.g., Installation labor"
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--main-background)', color: 'var(--main-text)', boxSizing: 'border-box', fontSize: '14px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'var(--main-text)', fontWeight: '600', fontSize: '14px' }}>Rate Type *</label>
                  <select
                    value={laborFormData.rateType}
                    onChange={(e) => setLaborFormData({ ...laborFormData, rateType: e.target.value as 'hourly' | 'per_sqft' })}
                    required
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--main-background)', color: 'var(--main-text)', boxSizing: 'border-box', fontSize: '14px' }}
                  >
                    <option value="hourly">Hourly Rate</option>
                    <option value="per_sqft">Per Square Foot</option>
                  </select>
                </div>

                {laborFormData.rateType === 'hourly' && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: 'var(--main-text)', fontWeight: '600', fontSize: '14px' }}>Hourly Rate ($) *</label>
                    <input
                      type="number"
                      value={laborFormData.hourlyRate}
                      onChange={(e) => setLaborFormData({ ...laborFormData, hourlyRate: e.target.value })}
                      placeholder="0.00"
                      step="0.01"
                      required={laborFormData.rateType === 'hourly'}
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--main-background)', color: 'var(--main-text)', boxSizing: 'border-box', fontSize: '14px' }}
                    />
                  </div>
                )}

                {laborFormData.rateType === 'per_sqft' && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: 'var(--main-text)', fontWeight: '600', fontSize: '14px' }}>Rate per Sq Ft ($) *</label>
                    <input
                      type="number"
                      value={laborFormData.perSqftRate}
                      onChange={(e) => setLaborFormData({ ...laborFormData, perSqftRate: e.target.value })}
                      placeholder="0.00"
                      step="0.01"
                      required={laborFormData.rateType === 'per_sqft'}
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--main-background)', color: 'var(--main-text)', boxSizing: 'border-box', fontSize: '14px' }}
                    />
                  </div>
                )}

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'var(--main-text)', fontWeight: '600', fontSize: '14px' }}>Markup Factor *</label>
                  <input
                    type="number"
                    value={laborFormData.markupFactor}
                    onChange={(e) => setLaborFormData({ ...laborFormData, markupFactor: e.target.value })}
                    placeholder="2.0"
                    step="0.1"
                    required
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--main-background)', color: 'var(--main-text)', boxSizing: 'border-box', fontSize: '14px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '22px' }}>
                    <input
                      type="checkbox"
                      checked={laborFormData.isActive}
                      onChange={(e) => setLaborFormData({ ...laborFormData, isActive: e.target.checked })}
                    />
                    <span style={{ color: 'var(--main-text)', fontWeight: '600', fontSize: '14px' }}>Active</span>
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '8px', gridColumn: '1 / -1' }}>
                  <button
                    type="submit"
                    style={{
                      padding: '8px 16px',
                      background: 'var(--primary-button)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                    }}
                  >
                    {editingLaborId ? 'Update' : 'Add'} Labor Rate
                  </button>
                  {showLaborForm && (
                    <button
                      type="button"
                      onClick={resetLaborForm}
                      style={{
                        padding: '8px 16px',
                        background: 'var(--page-background)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        color: 'var(--main-text)',
                        fontWeight: '500',
                        fontSize: '14px',
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Labor Rates List */}
            <div>
              <h3 style={{ margin: '0 0 12px 0', color: 'var(--main-text)', fontSize: '14px', fontWeight: '600' }}>Labor Rates ({laborRates.length})</h3>
              {laborRates.length === 0 ? (
                <p style={{ color: 'var(--secondary-text)', margin: 0 }}>No labor rates yet</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '12px' }}>
                  {laborRates.map((rate) => (
                    <div
                      key={rate.id}
                      style={{
                        padding: '12px',
                        background: 'var(--main-background)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        color: 'var(--main-text)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <p style={{ margin: '0 0 4px 0', fontWeight: '600', fontSize: '14px' }}>{rate.name}</p>
                          {rate.description && <p style={{ margin: 0, fontSize: '12px', color: 'var(--secondary-text)' }}>{rate.description}</p>}
                        </div>
                        <span style={{ fontSize: '12px', padding: '2px 8px', background: rate.isActive ? 'rgba(16, 185, 129, 0.2)' : 'rgba(107, 114, 128, 0.2)', color: rate.isActive ? 'var(--success-color)' : 'var(--secondary-text)', borderRadius: '4px' }}>
                          {rate.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--secondary-text)', marginBottom: '8px' }}>
                        <div>{rate.rateType === 'hourly' ? `$${rate.hourlyRate?.toFixed(2) || '0.00'}/hr` : `$${rate.perSqftRate?.toFixed(2) || '0.00'}/sqft`}</div>
                        <div>Markup: {rate.markupFactor}x ({((rate.markupFactor - 1) * 100).toFixed(0)}%)</div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleEditLaborRate(rate)}
                          style={{
                            padding: '4px 8px',
                            background: 'rgba(59, 130, 246, 0.1)',
                            color: 'var(--primary-button)',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500',
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteLaborRate(rate.id)}
                          style={{
                            padding: '4px 8px',
                            background: 'rgba(239, 68, 68, 0.1)',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500',
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
        {/* Branding Tab */}
        {activeTab === 'branding' && (
        <div
          style={{
            background: 'var(--card-background)',
            padding: '20px',
            marginBottom: '24px',
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
          }}
        >
          <h2 style={{ marginBottom: '16px', fontSize: '18px', color: 'var(--main-text)' }}>Branding</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px',
            }}
          >
            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Company Name
              </label>
              <input
                type="text"
                value={settings.branding.companyName}
                onChange={(e) =>
                  handleChange('branding', 'companyName', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Tagline
              </label>
              <input
                type="text"
                value={settings.branding.tagline}
                onChange={(e) =>
                  handleChange('branding', 'tagline', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: 'var(--main-text)', fontWeight: '600' }}>
                Logo (Image Upload)
              </label>
              <div
                style={{
                  border: '2px dashed var(--border-color)',
                  borderRadius: '6px',
                  padding: '20px',
                  textAlign: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                }}
              >
                <input
                  key="logo-input"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={uploadingLogo}
                  style={{
                    display: 'block',
                    margin: '0 auto',
                    cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                  }}
                />
                {uploadingLogo && (
                  <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: 'var(--secondary-text)', fontWeight: '500' }}>
                    Uploading...
                  </p>
                )}
                {!uploadingLogo && (
                  <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--secondary-text)' }}>
                    Max 5MB. PNG, JPG, etc.
                  </p>
                )}
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: 'var(--main-text)', fontWeight: '600' }}>
                Favicon (Image Upload)
              </label>
              <div
                style={{
                  border: '2px dashed var(--border-color)',
                  borderRadius: '6px',
                  padding: '20px',
                  textAlign: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                }}
              >
                <input
                  key="favicon-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFaviconUpload}
                  disabled={uploadingFavicon}
                  style={{
                    display: 'block',
                    margin: '0 auto',
                    cursor: uploadingFavicon ? 'not-allowed' : 'pointer',
                  }}
                />
                {uploadingFavicon && (
                  <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: 'var(--secondary-text)', fontWeight: '500' }}>
                    Uploading...
                  </p>
                )}
                {!uploadingFavicon && (
                  <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--secondary-text)' }}>
                    Max 5MB. PNG, JPG, ICO, etc.
                  </p>
                )}
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border-color)', paddingTop: '16px', marginTop: '8px' }}>
              <h3 style={{ marginBottom: '12px', color: 'var(--main-text)' }}>Login Page Text</h3>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Login Page Title
              </label>
              <input
                type="text"
                value={settings.branding.loginPageTitle}
                onChange={(e) =>
                  handleChange('branding', 'loginPageTitle', e.target.value)
                }
                placeholder="e.g., Redmax Quoting System"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Login Subtitle
              </label>
              <input
                type="text"
                value={settings.branding.loginPageSubtitle}
                onChange={(e) =>
                  handleChange('branding', 'loginPageSubtitle', e.target.value)
                }
                placeholder="e.g., Sign In"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Signup Button Text
              </label>
              <input
                type="text"
                value={settings.branding.loginSignupText}
                onChange={(e) =>
                  handleChange('branding', 'loginSignupText', e.target.value)
                }
                placeholder="e.g., Create Account"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>
          </div>
        </div>
        )}

        {/* Colors Tab */}
        {activeTab === 'colors' && (
        <div
          style={{
            background: 'var(--card-background)',
            padding: '20px',
            marginBottom: '24px',
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
          }}
        >
          <h2 style={{ marginBottom: '16px', fontSize: '18px', color: 'var(--main-text)' }}>Colors</h2>

          {/* Header/Navigation Colors */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: 'var(--main-text)' }}>
              Header/Navigation
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
              }}
            >
              {['headerBackground', 'headerText', 'headerButton'].map((colorKey) => (
                <div key={colorKey}>
                  <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                    {colorKey.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={(settings.colors as any)[colorKey]}
                      onChange={(e) =>
                        handleChange('colors', colorKey, e.target.value)
                      }
                      style={{
                        width: '50px',
                        height: '40px',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    />
                    <input
                      type="text"
                      value={(settings.colors as any)[colorKey]}
                      onChange={(e) =>
                        handleChange('colors', colorKey, e.target.value)
                      }
                      style={{
                        flex: 1,
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--main-background)',
                        color: 'var(--main-text)',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Main Content Colors */}
          <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid var(--border-color)' }}>
            <h3 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: 'var(--main-text)' }}>
              Main Content
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
              }}
            >
              {['mainBackground', 'mainText', 'secondaryText'].map((colorKey) => (
                <div key={colorKey}>
                  <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                    {colorKey.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={(settings.colors as any)[colorKey]}
                      onChange={(e) =>
                        handleChange('colors', colorKey, e.target.value)
                      }
                      style={{
                        width: '50px',
                        height: '40px',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    />
                    <input
                      type="text"
                      value={(settings.colors as any)[colorKey]}
                      onChange={(e) =>
                        handleChange('colors', colorKey, e.target.value)
                      }
                      style={{
                        flex: 1,
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--main-background)',
                        color: 'var(--main-text)',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cards/Windows Colors */}
          <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid var(--border-color)' }}>
            <h3 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: 'var(--main-text)' }}>
              Cards & Windows
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
              }}
            >
              {['cardBackground', 'cardText'].map((colorKey) => (
                <div key={colorKey}>
                  <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                    {colorKey.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={(settings.colors as any)[colorKey]}
                      onChange={(e) =>
                        handleChange('colors', colorKey, e.target.value)
                      }
                      style={{
                        width: '50px',
                        height: '40px',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    />
                    <input
                      type="text"
                      value={(settings.colors as any)[colorKey]}
                      onChange={(e) =>
                        handleChange('colors', colorKey, e.target.value)
                      }
                      style={{
                        flex: 1,
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--main-background)',
                        color: 'var(--main-text)',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid var(--border-color)' }}>
            <h3 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: 'var(--main-text)' }}>
              Buttons
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
              }}
            >
              {['primaryButton', 'primaryButtonText', 'secondaryButton', 'secondaryButtonText'].map((colorKey) => (
                <div key={colorKey}>
                  <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                    {colorKey.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={(settings.colors as any)[colorKey]}
                      onChange={(e) =>
                        handleChange('colors', colorKey, e.target.value)
                      }
                      style={{
                        width: '50px',
                        height: '40px',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    />
                    <input
                      type="text"
                      value={(settings.colors as any)[colorKey]}
                      onChange={(e) =>
                        handleChange('colors', colorKey, e.target.value)
                      }
                      style={{
                        flex: 1,
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--main-background)',
                        color: 'var(--main-text)',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* UI Elements */}
          <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid var(--border-color)' }}>
            <h3 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: 'var(--main-text)' }}>
              UI Elements
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
              }}
            >
              {['border', 'accent', 'iconColor'].map((colorKey) => (
                <div key={colorKey}>
                  <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                    {colorKey.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={(settings.colors as any)[colorKey]}
                      onChange={(e) =>
                        handleChange('colors', colorKey, e.target.value)
                      }
                      style={{
                        width: '50px',
                        height: '40px',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    />
                    <input
                      type="text"
                      value={(settings.colors as any)[colorKey]}
                      onChange={(e) =>
                        handleChange('colors', colorKey, e.target.value)
                      }
                      style={{
                        flex: 1,
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--main-background)',
                        color: 'var(--main-text)',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Status Colors */}
          <div>
            <h3 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: 'var(--main-text)' }}>
              Status Colors
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
              }}
            >
              {['success', 'warning', 'error'].map((colorKey) => (
                <div key={colorKey}>
                  <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                    {colorKey.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={(settings.colors as any)[colorKey]}
                      onChange={(e) =>
                        handleChange('colors', colorKey, e.target.value)
                      }
                      style={{
                        width: '50px',
                        height: '40px',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    />
                    <input
                      type="text"
                      value={(settings.colors as any)[colorKey]}
                      onChange={(e) =>
                        handleChange('colors', colorKey, e.target.value)
                      }
                      style={{
                        flex: 1,
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--main-background)',
                        color: 'var(--main-text)',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        )}

        {/* Typography Tab */}
        {activeTab === 'typography' && (
        <div
          style={{
            background: 'var(--card-background)',
            padding: '20px',
            marginBottom: '24px',
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
          }}
        >
          <h2 style={{ marginBottom: '16px', fontSize: '18px', color: 'var(--main-text)' }}>Typography</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px',
            }}
          >
            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Font Family
              </label>
              <select
                value={settings.typography.fontFamily}
                onChange={(e) =>
                  handleChange('typography', 'fontFamily', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                  fontFamily: settings.typography.fontFamily,
                  cursor: 'pointer',
                }}
              >
                <option value="Inter, sans-serif">Inter</option>
                <option value="'Segoe UI', sans-serif">Segoe UI</option>
                <option value="'Helvetica Neue', sans-serif">Helvetica Neue</option>
                <option value="Arial, sans-serif">Arial</option>
                <option value="'Times New Roman', serif">Times New Roman</option>
                <option value="Georgia, serif">Georgia</option>
                <option value="'Courier New', monospace">Courier New</option>
                <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                <option value="'Verdana', sans-serif">Verdana</option>
                <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                <option value="'Palatino Linotype', serif">Palatino Linotype</option>
                <option value="'Lucida Console', monospace">Lucida Console</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Body Size (px)
              </label>
              <input
                type="number"
                value={settings.typography.bodySize}
                onChange={(e) =>
                  handleChange('typography', 'bodySize', parseInt(e.target.value))
                }
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Heading Size (px)
              </label>
              <input
                type="number"
                value={settings.typography.headingSize}
                onChange={(e) =>
                  handleChange('typography', 'headingSize', parseInt(e.target.value))
                }
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>
          </div>

          <div
            style={{
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: '1px solid var(--border-color)',
            }}
          >
            <h3 style={{ marginBottom: '12px', fontSize: '14px', color: 'var(--main-text)' }}>Font Scale</h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '16px',
              }}
            >
              {['h1', 'h2', 'h3', 'body'].map((key) => (
                <div key={key}>
                  <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                    {key.toUpperCase()}
                  </label>
                  <input
                    type="number"
                    value={(settings.typography.fontScale as any)[key]}
                    onChange={(e) =>
                      handleNestedChange(
                        'typography',
                        'fontScale',
                        key,
                        parseInt(e.target.value)
                      )
                    }
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid var(--border-color)',
                      boxSizing: 'border-box',
                      backgroundColor: 'var(--main-background)',
                      color: 'var(--main-text)',
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        )}

        {/* Quotes Tab */}
        {activeTab === 'quotes' && (
        <div
          style={{
            background: 'var(--card-background)',
            padding: '20px',
            marginBottom: '24px',
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
          }}
        >
          <h2 style={{ marginBottom: '16px', fontSize: '18px', color: 'var(--main-text)' }}>Quote Settings</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px',
            }}
          >
            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                PDF Header Text
              </label>
              <input
                type="text"
                value={settings.quotes.pdfHeaderText}
                onChange={(e) =>
                  handleChange('quotes', 'pdfHeaderText', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Quote Prefix
              </label>
              <input
                type="text"
                value={settings.quotes.quotePrefix}
                onChange={(e) =>
                  handleChange('quotes', 'quotePrefix', e.target.value)
                }
                placeholder="e.g., QT-"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Currency Symbol
              </label>
              <input
                type="text"
                value={settings.quotes.currencySymbol}
                onChange={(e) =>
                  handleChange('quotes', 'currencySymbol', e.target.value)
                }
                placeholder="$"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Default Terms
              </label>
              <input
                type="text"
                value={settings.quotes.defaultTerms}
                onChange={(e) =>
                  handleChange('quotes', 'defaultTerms', e.target.value)
                }
                placeholder="e.g., Net 30"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                PDF Footer Text
              </label>
              <textarea
                value={settings.quotes.pdfFooterText}
                onChange={(e) =>
                  handleChange('quotes', 'pdfFooterText', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  minHeight: '60px',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Notes Template
              </label>
              <textarea
                value={settings.quotes.notesTemplate}
                onChange={(e) =>
                  handleChange('quotes', 'notesTemplate', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  minHeight: '80px',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Default Tax Rate (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.001"
                value={settings.quotes.defaultTaxRate || 0}
                onChange={(e) =>
                  handleChange('quotes', 'defaultTaxRate', parseFloat(e.target.value) || 0)
                }
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={settings.quotes.showMarkupDetails}
                  onChange={(e) =>
                    handleChange('quotes', 'showMarkupDetails', e.target.checked)
                  }
                />
                Show Markup Details
              </label>
            </div>
          </div>
        </div>
        )}

        {/* Email Tab */}
        {activeTab === 'email' && (
        <div
          style={{
            background: 'var(--card-background)',
            padding: '20px',
            marginBottom: '24px',
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
          }}
        >
          <h2 style={{ marginBottom: '16px', fontSize: '18px', color: 'var(--main-text)' }}>Email Settings</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px',
            }}
          >
            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Sender Email
              </label>
              <input
                type="email"
                value={settings.email.senderEmail}
                onChange={(e) =>
                  handleChange('email', 'senderEmail', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Sender Name
              </label>
              <input
                type="text"
                value={settings.email.senderName}
                onChange={(e) =>
                  handleChange('email', 'senderName', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Quote Subject Line
              </label>
              <input
                type="text"
                value={settings.email.quoteSubjectLine}
                onChange={(e) =>
                  handleChange('email', 'quoteSubjectLine', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '4px', color: 'var(--main-text)', fontWeight: '600' }}>
                Quote Email Template
              </label>
              <textarea
                value={settings.email.quoteEmailTemplate}
                onChange={(e) =>
                  handleChange('email', 'quoteEmailTemplate', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  minHeight: '100px',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--main-background)',
                  color: 'var(--main-text)',
                }}
              />
            </div>
          </div>
        </div>
        )}

        {activeTab === 'pdf' && (
          <div>
            <PDFTemplateSettings
              templates={settings.quotes.pdfTemplates || []}
              onTemplatesChange={(templates) => {
                setSettings({
                  ...settings,
                  quotes: {
                    ...settings.quotes,
                    pdfTemplates: templates,
                  },
                });
              }}
            />
          </div>
        )}

        {activeTab === 'roles' && (
          <div>
            <RoleSettings
              settings={settings.roles || {
                admin: {
                  canCreateProjects: true,
                  canEditProjects: true,
                  canDeleteProjects: true,
                  canExportPDF: true,
                  canViewMaterials: true,
                  canViewProcesses: true,
                  canViewCosts: true,
                  canViewMarkupDetails: true,
                  canViewAllProjects: true,
                  canViewOnlyOwnProjects: false,
                  canManageUsers: true,
                  canManageSettings: true,
                },
                sales: {
                  canCreateProjects: true,
                  canEditProjects: true,
                  canDeleteProjects: false,
                  canExportPDF: true,
                  canViewMaterials: true,
                  canViewProcesses: true,
                  canViewCosts: true,
                  canViewMarkupDetails: true,
                  canViewAllProjects: false,
                  canViewOnlyOwnProjects: true,
                  canManageUsers: false,
                  canManageSettings: false,
                },
                viewer: {
                  canCreateProjects: false,
                  canEditProjects: false,
                  canDeleteProjects: false,
                  canExportPDF: true,
                  canViewMaterials: true,
                  canViewProcesses: true,
                  canViewCosts: false,
                  canViewMarkupDetails: false,
                  canViewAllProjects: false,
                  canViewOnlyOwnProjects: true,
                  canManageUsers: false,
                  canManageSettings: false,
                },
              }}
              onSettingsChange={(roles) => {
                setSettings({
                  ...settings,
                  roles,
                });
              }}
            />
          </div>
        )}

        {/* Save Button */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            type="submit"
            style={{
              padding: '12px 24px',
              background: 'var(--primary-button)',
              color: 'var(--primary-button-text)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
            }}
          >
            Save Settings
          </button>
          <button
            type="button"
            onClick={() => loadSettings()}
            style={{
              padding: '12px 24px',
              background: '#f3f4f6',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
            }}
          >
            Reload
          </button>
        </div>
      </form>
      )}
    </div>
    </AppLayout>
  );
};
